import { JSX, SVGProps, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Spin, Table, TableColumnsType, Tag } from "antd";
import { CardContainer } from "../../components/Card/CardContainer";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Text from "../../components/Text/Text";
import Button from "../../components/Button/Button";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { GreyTableContainer } from "../../components/Table/GreyTable";
import { useLocalStorage } from "../../hook/useLocalStorage";
import useScrollToTop from "../../hook/useScrollToTop";
import color from "../../resource/color";
import { resolveShopTierConfig } from "../ShopTier/config";
import { generateShopTierMockData } from "../ShopTier/mockData";
import { actionColorOf } from "../ShopTier/actionColor";
import { tierAuditLogActivityLabel } from "../ShopTier/tierAuditLogActivity";
import {
  MonitorCard,
  MonitorFacts,
  ProgressTrack,
  ProgressBar,
  ProgressScale,
  NavCard,
  BenefitSection,
  BenefitList,
  kpiTextColorBoolean,
} from "../ShopTier/styled";
import {
  getCustomerTierDetail,
  getTierAuditLog,
  updateNavStatus,
  TierAuditLogItem,
} from "../../datasource/CustomerTierProgramDatasource";
import { useQuery, useQueryClient } from "react-query";
import { SHOP_TIER_PENDING_NAV_QUERY_KEY } from "../../hook/useShopTierPendingNav";
import { compactAmountFormatter, dateFormatter } from "../../utility/Formatter";
import { toRebatePercent } from "../ShopTierBenefit/rebatePercent";
import AntdTabs from "../../components/AntdTabs/AntdTabs";
import AccrualMovementTab from "./AccrualMovementTab";
import ClampedText from "../../components/ClampedText/ClampedText";
import TierTablePagination, { PAGE_SIZE_OPTIONS } from "../ShopTier/TierTablePagination";


const conditionKey: Record<string, string> = {
  TOTAL: "ยอดสะสมต่อปี",
  AFFILIATE_QTY: "ยอดขายปุ๋ยสะสมต่อปี",
  YAMA: "ยอดขายยาตราม้าบินสะสมต่อปี",
  HERO: "ยอดสินค้ากลุ่ม The Hero สะสมต่อปี",
};

function parseAmount(label: string): number {
  return parseFloat(label.replace(/,/g, "")) || 0;
}

const formatAccruedTotal = compactAmountFormatter;

function tierThreshold(subLabel: string): number {
  const match = subLabel.match(/[\d,]+(\.\d+)?/);
  return match ? parseFloat(match[0].replace(/,/g, "")) : 0;
}

function formatAmount(value: number, volumeUnit: string): string {
  return volumeUnit === "ล้านบาท" ? value.toFixed(1) : Math.round(value).toLocaleString("th-TH");
}

function CheckIcon({ size = 14, ...props }: SVGProps<SVGSVGElement> & { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

// A function because the Action label for CHANGE_REBATE_DISPLAY_POLICY depends on the company.
const auditLogColumns = (company?: string | null): TableColumnsType<TierAuditLogItem> => [
  {
    title: "วันที่",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (v: string) => (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Text fontFamily="Sarabun" fontSize={16} fontWeight={500} color="Text2">{v ? dateFormatter(v, true).split(" ")[0] : "-"}</Text>
        <Text fontFamily="Sarabun" fontSize={12} fontWeight={500} color="Text3">{v ? `${dateFormatter(v, true).split(" ")[1]} น.` : "-"}</Text>
      </div>
    ),
  },
  {
    title: "Action",
    dataIndex: "activity",
    key: "activity",
    render: (_,r) => {
      const label = tierAuditLogActivityLabel(r.activity, company);
      return (
        <Tag style={{ borderRadius: 16, border: "none" }} color={actionColorOf(r.activity,r.updatedBy)}>
          <Text fontFamily="Sarabun" fontSize={13} fontWeight={700} color="white">{label}</Text>
        </Tag>
      );
    },
  },
  {
    title: "ก่อน",
    dataIndex: "valueBefore",
    key: "valueBefore",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v || "-"}</Text>,
  },
  {
    title: "หลัง",
    dataIndex: "valueAfter",
    key: "valueAfter",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v || "-"}</Text>,
  },
  {
    title: "เหตุผล",
    dataIndex: "remark",
    key: "remark",
    width: 220,
    // Long reasons fold to 3 lines with "อ่านเพิ่มเติม" / "ย่อ"; shorter ones show no toggle.
    render: (v: string) =>
      v ? <ClampedText text={v} rows={3} /> : <Text fontFamily="Sarabun" fontSize={14} color="Text2">-</Text>,
  },
  {
    title: "ผู้ดำเนินการ",
    dataIndex: "updatedBy",
    key: "updatedBy",
    render: (v: string) => <Text fontFamily="Sarabun" fontSize={14} color="Text2">{v === "SYSTEM" ? "ระบบ" : v || "-"}</Text>,
  },
];

// Finds the first leaf with this conditionKey anywhere in the progress expression
// (HERO sits at the top level in an AND, but inside a group in an OR).
function findConditionNode(nodes: any[] | undefined, key: string): any | undefined {
  for (const node of nodes ?? []) {
    if (node?.conditionKey === key) return node;
    const nested = findConditionNode(node?.nodes, key);
    if (nested) return nested;
  }
  return undefined;
}

/**
 * "ยอดสะสมอีก … จึงถึงระดับ …" line under the progress bar. Reads what is still missing on the
 * TOTAL and HERO conditions and names only the ones that aren't met yet:
 *   TOTAL only → ยอดสะสมอีก X
 *   HERO only  → ยอดสินค้า Hero อีก Y
 *   both       → ยอดสะสมอีก X และยอดสินค้า Hero อีก Y
 * Nothing left on either → renders nothing.
 */
function RemainingToNextTier({
  nodes,
  company,
  tierName,
  year,
  isTopLevel
}: {
  nodes?: any[];
  company?: string;
  tierName?: string;
  year?: number;
  isTopLevel?: boolean;
}): JSX.Element | null {
  const totalRemaining = Number(findConditionNode(nodes, "TOTAL")?.remaining) || 0;
  const heroRemaining = Number(findConditionNode(nodes, "HERO")?.remaining) || 0;
  if (totalRemaining === 0 && heroRemaining === 0) return null;

  const amount = (value: number) => (
    <Text fontSize={12} color="Text1" fontWeight={700}>{compactAmountFormatter(company ?? "", value)}</Text>
  );
  if(company !== "ICPL" && isTopLevel) return (
    <Text fontSize={12} color="success" fontWeight={500}>
      ร้านถึงระดับสูงสุดแล้ว
    </Text>
  );
  return (
    <Text fontSize={12} color="Text1" fontWeight={500}>
      {totalRemaining !== 0 && <>ยอดสะสมอีก {amount(totalRemaining)}</>}
      {totalRemaining !== 0 && heroRemaining !== 0 && " และ"}
      {heroRemaining !== 0 && <>ยอดสินค้า Hero อีก {amount(heroRemaining)}</>}
      {" "}จึงถึงระดับ {tierName} {company === "ICPL" ? `ของปี ${year}` : ""}
    </Text>
  );
}

function ShopTierDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  // Arriving from the shop list (often scrolled down) must start at the top of this page.
  useScrollToTop(id);
  const [company] = useLocalStorage("profile", null);
  const config = resolveShopTierConfig(company?.company);
  const mockData = generateShopTierMockData(config);
  const shop = mockData.shops[0]
  const [showConfirmUpload, setShowConfirmUpload] = useState(false);
  const [showConfirmUndo, setShowConfirmUndo] = useState(false);

  const currentYearConfig = mockData.configYears.find((y) => y.year === "ปี 2569");
  const tierBenefit = currentYearConfig?.tiers.find((t) => t.tierKey === shop.tier);

  const tierIndex = config.tiers.findIndex((t) => t.key === shop.tier);
  const currentTier = config.tiers[tierIndex];
  const nextTier = config.tiers[tierIndex + 1];
  const currentMin = currentTier ? tierThreshold(currentTier.subLabel) : 0;
  const nextMin = nextTier ? tierThreshold(nextTier.subLabel) : currentMin;
  const volumeValue = parseAmount(shop.volumeLabel);
  const percentToNext = nextTier
    ? Math.min(100, Math.max(0, ((volumeValue - currentMin) / (nextMin - currentMin)) * 100))
    : 100;
  const remaining = nextTier ? Math.max(0, nextMin - volumeValue) : 0;

  const [auditLogPage, setAuditLogPage] = useState(1);
  const [auditLogTake, setAuditLogTake] = useState(PAGE_SIZE_OPTIONS[0]);
  const [historyTab, setHistoryTab] = useState("tier");

  useEffect(() => {
    setAuditLogPage(1);
  }, [id]);

  const { data, isLoading } = useQuery(
  ["shopDetail", id],
    async() => {
      const res = await getCustomerTierDetail(Number(id));
      setIsUploading(res?.responseData?.navision?.isUpdateNav ?? false);
      return res;
    },
    { enabled: !!id, refetchOnWindowFocus: false },
  );

  const { data : tierAuditLogData, isLoading: isTierAuditLogLoading } = useQuery(
  ["tierAuditLog", id, auditLogPage, auditLogTake],
    () => getTierAuditLog({
      company: company?.company,
      customerCompanyId: Number(id),
      activity: ["AUTO_PROMOTE","AUTO_DEMOTE","MANUAL_ADJUST","ROLLOVER","INITIAL_TIER"],
      page: auditLogPage,
      take: auditLogTake,
    }),
    { enabled: !!id, refetchOnWindowFocus: false, keepPreviousData: true },
  );

  // ยังไม่ยืนยัน response schema จริงกับ backend — สมมติตาม responseData.data/.count เหมือน getCustomerTierSummary
  const tierAuditLogList: TierAuditLogItem[] = tierAuditLogData?.responseData?.data || [];
  const tierAuditLogTotal: number = tierAuditLogData?.responseData?.count || 0;

  const shopDetail = data?.responseData;

  const [isUploading, setIsUploading] = useState(shopDetail?.navision?.isUpdateNav ?? false);

  const handleUpdateNavStatus = async (isUpdateNav: boolean) => {
    updateNavStatus(shopDetail?.customerTierId,isUpdateNav,company?.firstname + " " + company?.lastname).then(() => {
      queryClient.invalidateQueries([SHOP_TIER_PENDING_NAV_QUERY_KEY]);
      setIsUploading(isUpdateNav);
      setShowConfirmUpload(false);
      setShowConfirmUndo(false)
    });
  }
  if (isLoading) {
    return (
      <CardContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh",
          }}
        >
          <Spin size='large' />
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <PageTitleNested
        title={shopDetail?.customerName}
        showBack
        onBack={() => navigate(-1)}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ระดับร้านค้า", path: -1 },
              { text: "รายละเอียดร้านค้า", path: window.location.pathname },
            ]}
          />
        }
        description={
          <Text fontFamily="Sarabun" fontSize={13} color="Text3">
            รหัสร้านค้า {shopDetail?.customerNo} · จ.{shopDetail?.customerProvince} · {shopDetail?.customerZone}
          </Text>
        }
      />

      <MonitorCard style={{ marginTop: 20 }}>
        <MonitorFacts>
          <div>
            <Text fontSize={13} color="Text3">ระดับปัจจุบัน ปี {shopDetail?.programYear}</Text>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, marginBottom: 12 }}>
              <Tag style={{ borderRadius: 16, border: "none" }} color={shopDetail?.currentTier?.color || color.Grey}>
                <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color={kpiTextColorBoolean(shopDetail?.currentTier?.color) ? "Text4" : "white"}>{shopDetail?.currentTier?.tierName}</Text>
              </Tag>
            </div>
            {
              company?.company === "ICPL" ? (
                <Text fontSize={13} fontFamily="Sarabun" color="Text1">
                  เผยแพร่โดย {shopDetail?.tierChangedBy === "SYSTEM" ? "ระบบ" : shopDetail?.tierChangedBy} เมื่อ วันที่ {shopDetail?.tierChangedAt ? dateFormatter(shopDetail?.tierChangedAt) : "-"} 
                </Text>
              ) : <Text fontSize={13} fontFamily="Sarabun" color="Text1">
                ระบบเลื่อนให้เมื่อ {shopDetail?.tierChangedAt ? dateFormatter(shopDetail?.tierChangedAt) : "-"}
              </Text>
            }
          </div>
          <div>
            <Text fontSize={13} color="Text3">ยอดสะสมปี {shopDetail?.programYear}</Text>
            <div style={{ marginTop: 6 }}>
              <Text fontWeight={700} fontSize={20}>{compactAmountFormatter(company?.company, shopDetail?.accruedTotal)}</Text>
            </div>
            {tierBenefit && (
              <Text fontSize={12} color="Text3">
                {company?.company === "ICPF" ? "Top Up" : "Rebate"} ระดับนี้ {toRebatePercent(shopDetail?.currentTier?.rebatePercent)}%
              </Text>
            )}
          </div>
          <div>
            <Text fontSize={13} color="Text3">{company?.company === "ICPL" ? `ระดับคาดการณ์ ปี ${shopDetail?.programYear+1}` :"ระดับถัดไป"}</Text>
            <div style={{ marginTop: 6,marginBottom: 8 }}>
              {
                company?.company === "ICPL" ? 
                  <Tag style={{ borderRadius: 16, border: "none" }} color={shopDetail?.earnedTier?.color || color.Grey}>
                    <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color={kpiTextColorBoolean(shopDetail?.earnedTier?.color) ? "Text4" : "white"}>{shopDetail?.earnedTier?.tierName}</Text>
                  </Tag> : 
                  <>
                    {
                      !shopDetail?.nextTier ?
                        <Text fontSize={14} fontWeight={700} color="Text3">ระดับสูงสุดแล้ว</Text> :
                        <Tag style={{ borderRadius: 16, border: "none" }} color={shopDetail?.nextTier?.color || color.Grey}>
                          <Text fontFamily="Sarabun" fontSize={14} fontWeight={700} color={kpiTextColorBoolean(shopDetail?.nextTier?.color) ? "Text4" : "white"}>{shopDetail?.nextTier?.tierName}</Text>
                        </Tag>
                    }
                  </>
              }
            </div>
            <Text style={{ marginTop: 25 }} fontSize={12} color="Text3">{company?.company === "ICPL" ? `ประมาณการ ผลจริงตัดสินรอบปิดปี 1 ธ.ค. - 31 ม.ค.` : `เลื่อนอัตโนมัติเมื่อยอดถึงเกณฑ์`}</Text>
          </div>
        </MonitorFacts>
        <Divider />
        {/* <ProgressTrack>
          <ProgressBar $percent={percentToNext} />
        </ProgressTrack>
        <ProgressScale>
          <span>{formatAmount(currentMin, config.volumeUnit)} {config.volumeUnit}</span>
        </ProgressScale> */}
        {
          shopDetail?.progress?.expression?.operator === "AND" ? (<>
            {shopDetail?.progress?.expression?.nodes?.map((node, index) => (
              <div key={index}>
                {
                  node.conditionKey === "TOTAL" ? (<div>
                    <Text fontSize={12} color="Text3">
                      ความคืบหน้ายอดปี {shopDetail?.programYear} ที่จะตัดสินระดับปี {shopDetail?.programYear+1}</Text>
                    <ProgressTrack style={{ marginTop: 6 }}>
                      <ProgressBar $percent={(node.current - node.fromTarget) / (node.target- node.fromTarget) * 100} $color={
                        (node.current - node.fromTarget) / (node.target- node.fromTarget) >= 1 ? color.success : color.primary
                      }/>
                    </ProgressTrack>
                    <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                      <Text fontSize={12} color="Text3">
                        {compactAmountFormatter(company?.company, node.fromTarget)}
                      </Text>
                      <Text fontSize={12} color="Text3">
                        {compactAmountFormatter(company?.company, node.target)}
                      </Text>
                    </div>
                    <RemainingToNextTier
                      nodes={shopDetail?.progress?.expression?.nodes}
                      company={company?.company}
                      tierName={shopDetail?.progress?.tierName}
                      year={shopDetail?.programYear + 1}
                      isTopLevel={shopDetail?.currentTier?.isTopLevel}
                    />
                    <Divider style={{ marginTop: 24, marginBottom: 24 }} />
                  </div>) : (<div>
                    <Text fontSize={14} fontWeight={700} color="Text1">
                      เงื่อนไขเพิ่มเติมของระดับ {shopDetail?.progress?.tierName}</Text>
                    <ProgressTrack style={{ marginTop: 6 }}>
                      <ProgressBar $percent={(node.current - node.fromTarget) / (node.target- node.fromTarget) * 100} $color={
                        (node.current - node.fromTarget) / (node.target- node.fromTarget) >= 1 ? color.success : color.primary
                      }/>
                    </ProgressTrack>
                    <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                      <Text fontSize={12} color="Text3">
                        {conditionKey[node.conditionKey]}
                      </Text>
                      <Text fontSize={12} color="Text3">
                        {`${compactAmountFormatter(company?.company, node.remaining === 0 ? node.target : node.current)} / ${compactAmountFormatter(company?.company, node.target)}`}
                      </Text>
                    </div>
                  </div>)
                }
              </div>
            ))}
          </>) : (<>
            {shopDetail?.progress?.expression?.nodes?.map((node, index) => (
              <div key={index}>
                {
                  node.conditionKey === "TOTAL" ? (<div>
                    <Text fontSize={12} color="Text3">
                      ความคืบหน้ายอดปี {shopDetail?.programYear} ที่จะตัดสินระดับปี {shopDetail?.programYear+1}</Text>
                    <ProgressTrack style={{ marginTop: 6 }}>
                      <ProgressBar $percent={(node.current - node.fromTarget) / (node.target- node.fromTarget) * 100} $color={
                        (node.current - node.fromTarget) / (node.target- node.fromTarget) >= 1 ? color.success : color.primary
                      }/>
                    </ProgressTrack>
                    <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                      <Text fontSize={12} color="Text3">
                        {compactAmountFormatter(company?.company, node.fromTarget)}
                      </Text>
                      <Text fontSize={12} color="Text3">
                        {compactAmountFormatter(company?.company, node.target)}
                      </Text>
                    </div>
                    <RemainingToNextTier
                      nodes={shopDetail?.progress?.expression?.nodes}
                      company={company?.company}
                      tierName={shopDetail?.progress?.tierName}
                      year={shopDetail?.programYear + 1}
                      isTopLevel={shopDetail?.currentTier?.isTopLevel}
                    />
                    <Divider style={{ marginTop: 24, marginBottom: 24 }} />
                    <Text fontSize={14} color="Text2" fontWeight={700}>หรือเงื่อนไขพิเศษ {shopDetail?.progress?.expression?.nodes[shopDetail?.progress?.expression?.nodes.length - 1].achievedCount}/{shopDetail?.progress?.expression?.nodes[shopDetail?.progress?.expression?.nodes.length - 1].requiredCount}</Text>
                  </div>) : (<>
                    {
                      (node.nodes ?? []).map((item,index)=> (
                        <div key={index}>
                          {
                            item.type === "LEAF" || (item.nodes?.length ?? 0) < 2 ?
                            <>
                              <ProgressTrack style={{ marginTop: 6 }}>
                                <ProgressBar $percent={(item.current - item.fromTarget) / (item.target- item.fromTarget) * 100} $color={
                                  (item.current - item.fromTarget) / (item.target- item.fromTarget) >= 1 ? color.success : color.primary
                                }/>
                              </ProgressTrack>
                              <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                                <Text fontSize={12} color="Text3">
                                  {conditionKey[item.conditionKey]}
                                </Text>
                                <Text fontSize={12} color="Text3">
                                  {`${compactAmountFormatter(company?.company, item.remaining === 0 ? item.target : item.current)} / ${compactAmountFormatter(company?.company, item.target)}`}
                                </Text>
                              </div>
                            </> : <>
                              <ProgressTrack style={{ marginTop: 6 }}>
                                <ProgressBar $percent={(item.nodes[0].current - item.nodes[0].fromTarget) / (item.nodes[0].target- item.nodes[0].fromTarget) * 100} $color={
                                  (item.nodes[0].current - item.nodes[0].fromTarget) / (item.nodes[0].target- item.nodes[0].fromTarget) >= 1 ? color.success : color.primary
                                }/>
                              </ProgressTrack>
                              <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                                <Text fontSize={12} color="Text3">
                                  {conditionKey[item.nodes[0].conditionKey]}
                                </Text>
                                <Text fontSize={12} color="Text3">
                                  {`${(item.nodes[0].remaining === 0 ? item.nodes[0].target : item.nodes[0].current).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${item.nodes[0].target.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ตัน`}
                                </Text>
                              </div>
                              <Divider>
                                <Text fontSize={12} color="Text3" fontFamily="Sarabun">
                                  หรือ
                                </Text>
                              </Divider>
                              <ProgressTrack style={{ marginTop: 6 }}>
                                <ProgressBar $percent={(item.nodes[1].current - item.nodes[1].fromTarget) / (item.nodes[1].target- item.nodes[1].fromTarget) * 100} $color={
                                  (item.nodes[1].current - item.nodes[1].fromTarget) / (item.nodes[1].target- item.nodes[1].fromTarget) >= 1 ? color.success : color.primary
                                }/>
                              </ProgressTrack>
                              <div style={{ marginTop: 12, flexDirection: "row", display: "flex", justifyContent: "space-between" }}>
                                <Text fontSize={12} color="Text3">
                                  {conditionKey[item.nodes[1].conditionKey]}
                                </Text>
                                <Text fontSize={12} color="Text3">
                                  {`${compactAmountFormatter(company?.company, item.nodes[1].remaining === 0 ? item.nodes[1].target : item.nodes[1].current)} / ${compactAmountFormatter(company?.company, item.nodes[1].target)}`}
                                </Text>
                              </div>
                            </>
                          }
                        </div>
                      ))
                    }
                  </>)
                }
              </div>
            ))}
          </>)
        }
        
      </MonitorCard>

      <NavCard style={{ alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Text fontWeight={700}>การอัปเดต Navision</Text>
            <Tag style={{ borderRadius: 16, border: "none" }} color={isUploading ? color.greenBackground : color.orangeBackground}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: isUploading ? color.darkGreen : color.Disable }}>
                {isUploading && <CheckIcon />}
                <Text fontFamily="Sarabun" fontSize={12} fontWeight={700} color={isUploading ? "darkGreen" : "Disable"}>
                  {isUploading ? "อัปโหลดแล้ว" : "รออัปโหลด"}
                </Text>
              </div>
            </Tag>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginTop: 12 }}>
            <div>
              <Text fontSize={12} color="Text3">รหัสร้านค้า</Text>
              <div><Text fontWeight={700} fontSize={14}>{shopDetail?.customerNo}</Text></div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">ระดับที่ต้องคีย์</Text>
              <div>
                <Text fontWeight={700} fontSize={14}>
                  {!shopDetail?.navision?.navSyncedTier?.tierName ? "-" : shopDetail?.navision?.navSyncedTier?.tierName} → {shopDetail?.currentTier?.tierName}
                </Text>
              </div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">วันที่มีผล</Text>
              <div><Text fontWeight={700} fontSize={14}>{shopDetail?.tierChangedAt ? dateFormatter(shopDetail?.tierChangedAt) : "-"}</Text></div>
            </div>
            <div>
              <Text fontSize={12} color="Text3">% Rebate</Text>
              <div><Text fontWeight={700} fontSize={14}>{`${toRebatePercent(shopDetail?.currentTier?.rebatePercent)}%`}</Text></div>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <Text fontSize={12} color="Text3">
              {isUploading
                ? shopDetail?.navision?.navUpdatedAt && shopDetail?.navision?.navUpdatedBy
                  ? `อัปเดตแล้วโดย ${shopDetail?.navision?.navUpdatedBy} เมื่อ ${dateFormatter(shopDetail?.navision?.navUpdatedAt)}`
                  : "ทำเครื่องหมายว่าอัปโหลดแล้ว"
                : "ยังไม่ได้คีย์เข้า Navision ให้คีย์ข้อมูลชุดนี้ในระบบ Navision ก่อน แล้วจึงกลับมาทำเครื่องหมาย"}
            </Text>
          </div>
        </div>
        <Button
          typeButton={isUploading ? "primary-light" : "primary"}
          title={isUploading ? "ยกเลิกการทำเครื่องหมาย":"ทำเครื่องหมายว่าอัปโหลดแล้ว"}
          style={{ width: "auto" }}
          onClick={() => (isUploading ? setShowConfirmUndo(true) : setShowConfirmUpload(true))}
        />
      </NavCard>

      <ConfirmModal
        visible={showConfirmUpload}
        title="ยืนยันการอัปเดต Navision"
        desc={`ยืนยันว่าคีย์ระดับ ${shopDetail?.currentTier?.tierName} ของ ${shopDetail?.customerName} (${shopDetail?.customerNo}) เข้า Navision เรียบร้อยแล้ว บันทึกลง Audit Log ทุกครั้ง`}
        onConfirm={() => {
          handleUpdateNavStatus(true);
        }}
        onCancel={() => setShowConfirmUpload(false)}
      />

      <ConfirmModal
        visible={showConfirmUndo}
        title="ยืนยันการอัปเดต Navision"
        desc={`ยืนยันการยกเลิกเครื่องหมายของ ${shopDetail?.customerName} (${shopDetail?.customerNo}) ระบบจะกลับไปเป็น รออัปโหลด และบันทึกลง Audit Log`}
        onConfirm={() => {
          handleUpdateNavStatus(false);
        }}
        onCancel={() => setShowConfirmUndo(false)}
      />

      <BenefitSection>
        <NavCard style={{ flexDirection: "column", alignItems: "flex-start" }}>
        <Text fontWeight={700} fontSize={16}>สิทธิประโยชน์ของระดับปัจจุบัน</Text>
        <Text fontSize={12} color="Text3">ข้อมูลแสดงผล ตรงกับที่ร้านค้าเห็นในแอป</Text>
        <BenefitList style={{ marginTop: 0 }}>
          {shopDetail?.benefits?.length > 0 ? shopDetail.benefits.map((b, index) => (
            <li key={b.benefitId ?? index}>
              <Text fontFamily="Sarabun" fontSize={14} color="Text2">{b.benefitName}</Text>
            </li>
          )) : <li><Text fontFamily="Sarabun" fontSize={14} color="Text2">ระดับนี้ยังไม่มีสิทธิประโยชน์ในตารางของทางบริษัท</Text></li>}
        </BenefitList>
        </NavCard>
      </BenefitSection>

      <Text fontWeight={700} fontSize={14}>ประวัติทั้งหมด</Text>
      <AntdTabs
        defaultTab={historyTab}
        onChange={setHistoryTab}
        data={[
          {
            key: "tier",
            label: "ประวัติระดับ",
            children: (
              <>
              <GreyTableContainer>
                <Table
                  rowKey={(r: TierAuditLogItem) => r.tierAuditLogId}
                  dataSource={tierAuditLogList}
                  columns={auditLogColumns(company?.company)}
                  loading={isTierAuditLogLoading}
                  pagination={false}
                />
              </GreyTableContainer>
              {/* Same pager as the other ShopTier tables (and the "การเคลื่อนไหวยอด" tab). */}
              <TierTablePagination
                current={auditLogPage}
                pageSize={auditLogTake}
                total={tierAuditLogTotal}
                onChange={setAuditLogPage}
                onPageSizeChange={(size) => {
                  setAuditLogTake(size);
                  setAuditLogPage(1);
                }}
              />
              </>
            ),
          },
          {
            key: "accrual",
            label: "การเคลื่อนไหวยอด",
            children: <AccrualMovementTab customerCompanyId={Number(id)} company={company?.company ?? ""} />,
          },
        ]}
      />
    </CardContainer>
  );
}

export default ShopTierDetailPage;

